import React, { Component } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import { isEmailValid } from "../../shared/utils/validation";
import { isPhoneNumberValid } from "../../shared/utils/numberUtils";

class ReferrerAgentAndClientDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      agentName: this.props.agentName ? this.props.agentName : "",
      agentEmailAddress: this.props.agentEmailAddress ? this.props.agentEmailAddress : "",
      clientFirstName: this.props.personalDetails ? this.props.personalDetails.firstName : "",
      clientLastName: this.props.personalDetails ? this.props.personalDetails.lastName : "",
      clientEmailAddress: this.props.personalDetails ? this.props.personalDetails.emailAddress : "",
      clientPhoneNumber: this.props.personalDetails ? this.props.personalDetails.phoneNumber : "",
      errors: {},
    };
  }

  // The agent's details can arrive after this step mounts (a signed-in agent's session lookup
  // resolving late). Adopt them only while the field is still empty, never over anything typed.
  componentDidUpdate(prevProps) {
    const adopt = {};
    if (this.props.agentName !== prevProps.agentName && this.state.agentName === "" && this.props.agentName) {
      adopt.agentName = this.props.agentName;
    }
    if (
      this.props.agentEmailAddress !== prevProps.agentEmailAddress &&
      this.state.agentEmailAddress === "" &&
      this.props.agentEmailAddress
    ) {
      adopt.agentEmailAddress = this.props.agentEmailAddress;
    }
    if (Object.keys(adopt).length > 0) {
      this.setState(adopt);
    }
  }

  agentNameEntered = (rn) => {
    this.setState({ agentName: rn });
  };

  agentEmailAddressEntered = (rn) => {
    this.setState({ agentEmailAddress: rn });
  };

  clientFirstNameEntered = (s) => {
    this.setState({ clientFirstName: s });
  };

  clientLastNameEntered = (s) => {
    this.setState({ clientLastName: s });
  };

  clientEmailAddressEntered = (s) => {
    this.setState({ clientEmailAddress: s });
  };

  clientPhoneEntered = (s) => {
    this.setState({ clientPhoneNumber: s });
  };

  handleValidation = () => {
    const fields = { ...this.state };
    let errors = {};
    let formIsValid = true;

    if (fields["agentName"] === "") {
      errors["agentName"] = true;
      formIsValid = false;
    }

    if (fields["agentEmailAddress"] === "" || !isEmailValid(fields["agentEmailAddress"])) {
      errors["agentEmailAddress"] = "Enter a valid email address";
      formIsValid = false;
    }

    if (fields["clientFirstName"] === "") {
      errors["clientFirstName"] = true;
      formIsValid = false;
    }

    if (fields["clientLastName"] === "") {
      errors["clientLastName"] = true;
      formIsValid = false;
    }

    if (fields["clientEmailAddress"] === "" || !isEmailValid(fields["clientEmailAddress"])) {
      errors["clientEmailAddress"] = "Enter a valid email address";
      formIsValid = false;
    }

    if (
      fields["clientEmailAddress"] !== "" &&
      fields["agentEmailAddress"] !== "" &&
      String(fields["clientEmailAddress"]).toLowerCase() === String(fields["agentEmailAddress"]).toLowerCase()
    ) {
      errors["clientEmailAddress"] = "Use your client’s own email address, not yours";
      errors["agentEmailAddress"] = "Use a different email address from your client’s";
      formIsValid = false;
    }

    if (fields["clientPhoneNumber"] === "" || !isPhoneNumberValid(fields["clientPhoneNumber"])) {
      errors["clientPhoneNumber"] = true;
      formIsValid = false;
    }

    this.setState({ errors });
    return formIsValid;
  };

  handleEmailAddressValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors[field] = false;
      } else {
        if (!prevState[field] || !isEmailValid(prevState[field])) {
          errors[field] = "Enter a valid email address";
        }
      }
      return { errors };
    });
  };

  handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors[field] = false;
      } else {
        if (!prevState[field]) {
          errors[field] = true;
        }
      }
      return { errors };
    });
  };

  handlePhoneNumberValidation = (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors.clientPhoneNumber = false;
      } else {
        if (!prevState.clientPhoneNumber || !isPhoneNumberValid(prevState.clientPhoneNumber)) {
          errors.clientPhoneNumber = true;
        }
      }
      return { errors };
    });
  };

  saveDetails = () => {
    if (this.handleValidation()) {
      this.props.onStepComplete({
        agentName: this.state.agentName,
        agentEmailAddress: this.state.agentEmailAddress,
        personalDetails: {
          firstName: this.state.clientFirstName,
          lastName: this.state.clientLastName,
          emailAddress: this.state.clientEmailAddress,
          phoneNumber: this.state.clientPhoneNumber,
        },
      });
    }
  };

  render() {
    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Your details" removeTopPadding>
          <p>So we can credit you with this referral and keep you updated.</p>
          <TextQuestion
            question="Your name"
            autoComplete="name"
            value={this.state.agentName}
            valueEntered={this.agentNameEntered}
            validate={this.handleFieldValidation("agentName")}
            validationError={this.state.errors["agentName"] ? "Enter your name" : null}
          />
          <TextQuestion
            question="Your work email"
            inputMode="email"
            autoComplete="email"
            value={this.state.agentEmailAddress}
            valueEntered={this.agentEmailAddressEntered}
            validate={this.handleEmailAddressValidation("agentEmailAddress")}
            validationError={this.state.errors.agentEmailAddress ? this.state.errors.agentEmailAddress : null}
          />
        </Card>

        <Card title="Your client’s details" removeTopPadding>
          <p>We’ll email them the quote and call to talk it through.</p>
          <TextQuestion
            question="First name"
            value={this.state.clientFirstName}
            valueEntered={this.clientFirstNameEntered}
            validate={this.handleFieldValidation("clientFirstName")}
            validationError={this.state.errors["clientFirstName"] ? "Enter their first name" : null}
          />

          <TextQuestion
            question="Last name"
            value={this.state.clientLastName}
            valueEntered={this.clientLastNameEntered}
            validate={this.handleFieldValidation("clientLastName")}
            validationError={this.state.errors.clientLastName ? "Enter their last name" : null}
          />

          <TextQuestion
            question="Email (for the quote)"
            inputMode="email"
            value={this.state.clientEmailAddress}
            valueEntered={this.clientEmailAddressEntered}
            validate={this.handleEmailAddressValidation("clientEmailAddress")}
            validationError={this.state.errors.clientEmailAddress ? this.state.errors.clientEmailAddress : null}
          />

          <TextQuestion
            question="Mobile or phone (so we can call about the quote)"
            type="tel"
            value={this.state.clientPhoneNumber}
            valueEntered={this.clientPhoneEntered}
            validate={this.handlePhoneNumberValidation}
            validationError={this.state.errors.clientPhoneNumber ? "Enter a valid phone number" : null}
          />

          <p>By continuing you confirm your client has agreed to you sharing their details with Juno.</p>

          <Action onClick={this.saveDetails}>Continue</Action>
        </Card>

        <Image src={imageOfficeWorking} fullWidth />
      </React.Fragment>
    );
  }
}

export default ReferrerAgentAndClientDetails;
